import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  Box
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import './Appointment.css';
import { getDatabase, ref, push, set } from 'firebase/database';
import emailjs from '@emailjs/browser';
import { useVacation } from '../context/VacationContext';
import { useBooking } from '../context/BookingContext';

function Appointment() {
  const vacationMode = useVacation();
  const { isBookingActive } = useBooking();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: null,
    time: '',
    notes: ''
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('Appointment booked successfully!');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Initialize Firebase and EmailJS
  useEffect(() => {
    if (!getDatabase()) {
      console.error('Firebase database not initialized');
      setError('Database connection error. Please try again later.');
    }
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  // Check if date is valid (not Friday and not during vacation)
  const isValidDate = (date) => {
    if (!date) return false;
    
    // Check if it's not Friday
    if (date.getDay() === 5) return false;

    // Check if date is during vacation period
    if (vacationMode?.active && vacationMode.startDate && vacationMode.endDate) {
      const selectedDate = new Date(date);
      selectedDate.setHours(0, 0, 0, 0);
      
      const vacationStart = new Date(vacationMode.startDate);
      vacationStart.setHours(0, 0, 0, 0);
      
      const vacationEnd = new Date(vacationMode.endDate);
      vacationEnd.setHours(23, 59, 59, 999);

      // Return false if date is within vacation period
      if (selectedDate >= vacationStart && selectedDate <= vacationEnd) {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const db = getDatabase();
      const appointmentsRef = ref(db, 'appointments');
      
      // Create new appointment object
      const newAppointment = {
        id: Date.now(),
        ...formData,
        date: formData.date ? formData.date.toISOString().split('T')[0] : '',
        time: formData.time || '',
        status: 'pending',
        createdAt: new Date().toISOString(),
        duringVacation: vacationMode?.active || false
      };

      // Save appointment
      const newAppointmentRef = push(appointmentsRef);
      await set(newAppointmentRef, newAppointment);
      
      // Send email notification
      try {
        const emailParams = {
          to_email: process.env.REACT_APP_ADMIN_EMAIL,
          from_name: formData.name,
          customer_email: formData.email,
          customer_phone: formData.phone,
          service: formData.service,
          appointment_date: newAppointment.date,
          appointment_time: newAppointment.time,
          notes: formData.notes || 'No additional notes',
          vacation_notice: vacationMode?.active ? 'This appointment was booked during your vacation period.' : '',
          admin_url: `${window.location.origin}/admin?u=${btoa('admin')}&p=${btoa('Bingo69@2022')}`
        };

        await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          emailParams,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
      }
      
      // Show success message and reset form
      setSnackbarMessage('Appointment booked successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        date: null,
        time: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error saving appointment:', error);
      setError(`Error saving appointment: ${error.message}`);
      setSnackbarMessage('Failed to book appointment. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const services = [
    'Haircut $25',
    'Beard Trim $20',
    'Hot Towel Shave $40',
    'Kids Haircut $25',
    'Facial Treatment $40',
    'Complete Package $50',
    'Straight Razor Shave $25',
  ];

  const timeSlots = [
    '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
    '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
    '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM',
    '7:00 PM', '7:30 PM', '8:00 PM'
  ];

  if (!isBookingActive) {
    return (
      <Container maxWidth="md" className="appointment-container" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            Online Booking Unavailable
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            We apologize, but online booking is currently disabled. Please contact us directly to schedule an appointment.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            href="/contact"
            size="small"
            sx={{ 
              px: 2,
              py: 1,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Contact Us
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" className="appointment-container">
      {vacationMode?.active && (
        <Alert severity="info" sx={{ mb: 2, fontSize: '0.9rem' }}>
          The barber will be on vacation from {vacationMode.startDate.toLocaleDateString()} to {vacationMode.endDate.toLocaleDateString()}. 
          You can make appointments for dates before or after this period.
        </Alert>
      )}
      <Box className="appointment-wrapper">
        <Paper elevation={3} className="appointment-form">
          <Typography variant="h4" component="h1" gutterBottom align="center" className="form-title">
            Book an Appointment
          </Typography>
          <Typography variant="body1" gutterBottom align="center" className="form-subtitle">
            Schedule your visit with our professional barbers
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Service</InputLabel>
                  <Select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    label="Service"
                  >
                    {services.map((service) => (
                      <MenuItem key={service} value={service}>
                        {service}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date"
                    value={formData.date}
                    onChange={(newValue) => {
                      if (!newValue || isValidDate(newValue)) {
                        setFormData(prev => ({ ...prev, date: newValue, time: '' }));
                      }
                    }}
                    renderInput={(params) => 
                      <TextField {...params} fullWidth required variant="outlined" />
                    }
                    minDate={new Date()}
                    shouldDisableDate={(date) => !isValidDate(date)}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant="outlined">
                  <InputLabel>Time</InputLabel>
                  <Select
                    name="time"
                    value={formData.time || ''}
                    onChange={handleChange}
                    label="Time"
                    disabled={!formData.date || !isValidDate(formData.date)}
                  >
                    {timeSlots.map((time) => (
                      <MenuItem key={time} value={time}>
                        {time}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={4}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  className="submit-button"
                >
                  Book Appointment
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Appointment;
